import React, { useEffect, useState } from "react";
import { Menu, X, MessageCircle, Ship } from "lucide-react";
import { FaFacebookF, FaGoogle, FaUsers } from "react-icons/fa";
import { NAV_ITEMS, SITE, buildWhatsAppLink } from "../mock/mock";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("accueil");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setActive(id);
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button onClick={() => handleNav("accueil")} className="flex items-center gap-3">
            <div className="relative">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0B2545] to-[#134074] flex items-center justify-center shadow-md">
                <Ship className="h-6 w-6 text-[#FFC93C]" />
              </div>
            </div>
            <div className="text-left leading-tight">
              <div className="font-black text-[#0B2545] text-lg tracking-tight">
                {SITE.name}
              </div>
              <div className="text-[10px] font-semibold text-[#0B2545]/70 tracking-widest">
                {SITE.tagline}
              </div>
              <div className="text-[10px] italic text-[#25D366] font-medium">
                {SITE.subtitle}
              </div>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className={`relative whitespace-nowrap px-3 py-2 text-[13px] font-bold tracking-wider transition-colors duration-200 ${
                  active === item.id ? "text-[#25D366]" : "text-[#0B2545] hover:text-[#25D366]"
                }`}
              >
                {item.label}
                {active === item.id && (
                  <span className="absolute left-3 right-3 -bottom-1 h-[3px] bg-[#25D366] rounded-full" />
                )}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-2">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full bg-[#1877F2] flex items-center justify-center text-white hover:scale-110 transition-transform"
                aria-label="Facebook"
              >
                <FaFacebookF className="h-4 w-4" />
              </a>
              <a
                href="#apropos"
                onClick={(e) => {
                  e.preventDefault();
                  handleNav("apropos");
                }}
                className="h-9 w-9 rounded-full bg-[#25D366] flex items-center justify-center text-white hover:scale-110 transition-transform"
                aria-label="Communauté"
              >
                <FaUsers className="h-4 w-4" />
              </a>
              <a
                href="https://google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center hover:scale-110 transition-transform"
                aria-label="Google"
              >
                <FaGoogle className="h-4 w-4 text-[#4285F4]" />
              </a>
            </div>
            <a
              href={buildWhatsAppLink("Bonjour, je souhaite réserver une excursion.")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-4 py-2.5 rounded-full shadow-lg shadow-[#25D366]/30 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap"
            >
              <MessageCircle className="h-4 w-4" fill="white" strokeWidth={0} />
              <span className="text-xs tracking-wide">RÉSERVER SUR WHATSAPP</span>
            </a>
          </div>

          {/* Mobile burger */}
          <button
            className="lg:hidden p-2 rounded-md text-[#0B2545]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden pb-4 border-t border-gray-100 pt-3">
            <nav className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={`text-left px-3 py-2 rounded-lg text-sm font-bold ${
                    active === item.id
                      ? "bg-[#25D366]/10 text-[#25D366]"
                      : "text-[#0B2545] hover:bg-gray-50"
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <a
                href={buildWhatsAppLink("Bonjour, je souhaite réserver une excursion.")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold px-4 py-3 rounded-full"
              >
                <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
                RÉSERVER SUR WHATSAPP
              </a>
            </nav>
          </div>
        )}
      </div>

      {/* Curved bottom accent */}
      <div className="h-3 bg-gradient-to-r from-[#0B2545] via-[#134074] to-[#0B2545]" />
    </header>
  );
};

export default Header;
